import { ArrowLeft, Clock, Package, Loader2, CheckCircle2, XCircle, MessageSquare, Trash2, ShoppingCart } from "lucide-react";
import { useState, useEffect } from "react";
import { BottomNav } from "./bottom-nav";
import { Button } from "./button";
import { AdImage } from "@/app/components/common/ad-image";
import { CollapsibleNestedOffer } from "@/app/components/collapsible-nested-offer";
import { ParentOfferBlock } from "@/app/components/parent-offer-block";
import { WithdrawOfferConfirmToast } from "@/app/components/withdraw-offer-confirm-toast";
import { getAdImage } from "@/app/utils/ad-utils";
import { getThreadViewItems } from "@/app/utils/offer-utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/components/ui/avatar";
import * as authClient from "@/app/api/auth-client";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";
import { toast } from "sonner";
import type { Ad } from "./ad-card";
import { toastNextTask } from "@/app/utils/toast-next-task";

interface OfferUpdate {
  type: "sent" | "counter-offer-received" | "accepted" | "rejected";
  message?: string;
  counterOfferAmount?: number;
  timestamp: Date;
}

interface SentOffer {
  id: string;
  sellerName: string;
  sellerAvatar: string;
  sellerProfileImageUrl?: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  message?: string;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "counter-offered" | "in-cart" | "completed" | "fulfilled" | "expired" | "cancelled";
  createdAt: Date;
  acceptedAt?: Date;
  expiresAt?: Date;
  updates: OfferUpdate[];
  /** Set when this offer is a counter (seller's proposal); buyer can accept it. */
  parentOfferId?: string;
  /** True when current user is the buyer (show "Add to cart"); false when seller (show "Contact the buyer"). */
  isCurrentUserBuyer?: boolean;
}

interface SentOffersPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onAdClick?: (adId: string) => void;
  sentOffers?: SentOffer[];
  /** When provided, load sent offers from API on mount. */
  loadSentOffers?: () => Promise<void | unknown>;
  onAddOfferToCart?: (offerId: string) => void;
  /** Offer IDs that are already in the cart (show "Remove" instead of "Add to cart"). */
  offerIdsInCart?: string[];
  /** When set and the offer is in the cart, checkout is bound to a server order — hide add/remove to avoid duplicate checkout. */
  cartOrderId?: string | null;
  onRemoveOfferFromCart?: (offerId: string) => void;
  /** Call after withdraw success to refresh notification bell count. */
  onNotificationCountRefetch?: () => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export function SentOffersPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onAdClick,
  sentOffers,
  loadSentOffers,
  onAddOfferToCart,
  offerIdsInCart = [],
  cartOrderId = null,
  onRemoveOfferFromCart,
  onNotificationCountRefetch,
}: SentOffersPageProps) {
  const accountNav = useAccountNavEnabled();
  const [loading, setLoading] = useState(!!loadSentOffers);
  const [offers, setOffers] = useState<SentOffer[]>(sentOffers ?? []);
  const [filter, setFilter] = useState<"all" | "pending" | "accepted" | "rejected" | "counter-offered">("all");
  const [withdrawingOfferId, setWithdrawingOfferId] = useState<string | null>(null);
  const [acceptingOfferId, setAcceptingOfferId] = useState<string | null>(null);
  const [showWithdrawConfirmToast, setShowWithdrawConfirmToast] = useState(false);
  const [withdrawConfirmOfferId, setWithdrawConfirmOfferId] = useState<string | null>(null);

  useEffect(() => {
    setOffers(sentOffers ?? []);
  }, [sentOffers]);

  useEffect(() => {
    if (loadSentOffers) {
      setLoading(true);
      loadSentOffers().finally(() => setLoading(false));
    }
  }, [loadSentOffers]);

  useEffect(() => {
    if (!accountNav) {
      setShowWithdrawConfirmToast(false);
      setWithdrawConfirmOfferId(null);
    }
  }, [accountNav]);

  const openWithdrawConfirm = (offerId: string) => {
    if (!accountNav || !loadSentOffers) return;
    setWithdrawConfirmOfferId(offerId);
    setShowWithdrawConfirmToast(true);
  };

  const closeWithdrawConfirmToast = () => {
    setShowWithdrawConfirmToast(false);
    setWithdrawConfirmOfferId(null);
  };

  const handleWithdrawConfirm = async () => {
    if (!accountNav || !loadSentOffers || !withdrawConfirmOfferId) return;
    setWithdrawingOfferId(withdrawConfirmOfferId);
    try {
      await authClient.withdrawOffer(withdrawConfirmOfferId);
      await loadSentOffers();
      onNotificationCountRefetch?.();
      closeWithdrawConfirmToast();
      toastNextTask(() => toast.success("Oferta retirada."));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toastNextTask(() => toast.error("Você não tem permissão para retirar esta oferta."));
      } else if (status === 400) {
        toastNextTask(() => toast.error("Esta oferta não está mais pendente."));
      } else {
        toastNextTask(() =>
          toast.error((err as Error)?.message ?? "Não foi possível retirar a oferta.")
        );
      }
    } finally {
      setWithdrawingOfferId(null);
    }
  };

  const handleAcceptOffer = async (offerId: string) => {
    if (!accountNav || !loadSentOffers) return;
    setAcceptingOfferId(offerId);
    try {
      await authClient.acceptOffer(offerId);
      await loadSentOffers();
      onNotificationCountRefetch?.();
      toastNextTask(() => toast.success("Contra-oferta aceita."));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toastNextTask(() => toast.error("Você não tem permissão para aceitar esta oferta."));
      } else if (status === 400) {
        toastNextTask(() => toast.error("Esta oferta não está mais pendente."));
      } else {
        toastNextTask(() =>
          toast.error((err as Error)?.message ?? "Não foi possível aceitar a contra-oferta.")
        );
      }
    } finally {
      setAcceptingOfferId(null);
    }
  };

  const filteredOffers = offers.filter((offer) => {
    if (filter === "all") return true;
    if (filter === "rejected") return offer.status === "rejected" || offer.status === "cancelled";
    if (filter === "counter-offered") return offer.status === "counter-offered";
    if (filter === "accepted") return offer.status === "accepted" || offer.status === "in-cart";
    return offer.status === filter;
  });

  const getStatusBadge = (status: SentOffer["status"]) => {
    const pill = "px-2 py-1 rounded-full text-xs whitespace-nowrap shrink-0";
    switch (status) {
      case "pending":
        return <span className={`${pill} bg-yellow-500/20 text-yellow-700 dark:text-yellow-300`}>Pendente</span>;
      case "accepted":
        return <span className={`${pill} bg-green-500/20 text-green-700 dark:text-green-300`}>Aceita</span>;
      case "rejected":
        return <span className={`${pill} bg-red-500/20 text-red-700 dark:text-red-300`}>Recusada</span>;
      case "cancelled":
        return <span className={`${pill} bg-gray-500/20 text-gray-700 dark:text-gray-300`}>Retirada</span>;
      case "counter-offered":
        return (
          <span className={`${pill} bg-blue-500/20 text-blue-700 dark:text-blue-300`}>Contra-oferta recebida</span>
        );
      case "in-cart":
        return <span className={`${pill} bg-emerald-500/20 text-emerald-800 dark:text-emerald-300`}>No carrinho</span>;
      case "fulfilled":
      case "completed":
        return <span className={`${pill} bg-blue-500/20 text-blue-700 dark:text-blue-300`}>Concluída</span>;
      case "expired":
        return <span className={`${pill} bg-gray-500/20 text-gray-700 dark:text-gray-300`}>Expirada</span>;
      default:
        return <span className={`${pill} bg-muted text-muted-foreground`}>{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Ofertas Enviadas</h1>
            <p className="text-sm text-muted-foreground">
              {filteredOffers.length} {filteredOffers.length === 1 ? "oferta" : "ofertas"}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "all" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "pending" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setFilter("accepted")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "accepted" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Aceitas
          </button>
          <button
            onClick={() => setFilter("rejected")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "rejected" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Recusadas
          </button>
          <button
            onClick={() => setFilter("counter-offered")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "counter-offered" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Contra-oferta
          </button>
        </div>
      </div>

      {/* Offers List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-3" />
            <p className="text-muted-foreground">Carregando ofertas...</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma oferta encontrada</p>
          </div>
        ) : (
          getThreadViewItems(filteredOffers).map((item) => {
            const offer = item.type === "single" ? item.offer : item.currentOffer;
            const inCartForThisThread =
              offerIdsInCart.includes(offer.id) ||
              (offer.parentOfferId != null && offerIdsInCart.includes(offer.parentOfferId));
            const checkoutLocked = Boolean(cartOrderId && inCartForThisThread);
            const hideOfferCartActions =
              offer.status === "fulfilled" ||
              offer.status === "completed" ||
              offer.status === "in-cart" ||
              checkoutLocked;
            const card = (
              <div
                className={`rounded-xl p-4 space-y-4 border-2 transition-all duration-300 ${
                  offer.status === "accepted" || offer.status === "in-cart"
                    ? "bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-orange-500/10 border-orange-300/50 shadow-lg shadow-orange-500/20"
                    : "bg-card border-border"
                }`}
              >
              {/* Accepted / checkout started: buyer CTA hidden when in-cart or order-linked */}
              {(offer.status === "accepted" || offer.status === "in-cart") && (
                <div className="space-y-2">
                  <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 text-white px-4 py-2 rounded-lg -mt-4 -mx-4 mb-2 flex items-center justify-center gap-2 shadow-md">
                    <CheckCircle2 className="w-5 h-5 shrink-0" />
                    <span className="font-medium text-center">
                      {offer.isCurrentUserBuyer
                        ? accountNav
                          ? hideOfferCartActions
                            ? checkoutLocked
                              ? "Oferta aceita! Pedido em andamento — continue no carrinho ou em Compras."
                              : offer.status === "in-cart"
                                ? "Pedido em andamento — continue em Compras ou no carrinho."
                                : "Oferta aceita."
                            : inCartForThisThread
                              ? "Oferta Aceita! Esta oferta já está no seu carrinho"
                              : "Oferta Aceita! Agora você pode adicionar ao carrinho"
                          : "Oferta aceita! Complete seu cadastro para adicionar ao carrinho."
                        : accountNav
                          ? "Oferta Aceita! Você pode entrar em contato com o comprador"
                          : "Oferta aceita! Complete seu cadastro para conversar com o comprador no chat."}
                    </span>
                  </div>
                </div>
              )}

              {/* Seller Info & Status */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar className="w-10 h-10 shrink-0">
                    {offer.sellerProfileImageUrl ? (
                      <AvatarImage src={offer.sellerProfileImageUrl} alt={offer.sellerName} />
                    ) : null}
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                      {offer.sellerAvatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{offer.sellerName}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(offer.createdAt)}</p>
                  </div>
                </div>
                <div className="shrink-0 pt-0.5">{getStatusBadge(offer.status)}</div>
              </div>

              {/* Target Ad - Now Clickable */}
              <button
                onClick={() => onAdClick?.(offer.targetAd.id)}
                className="bg-accent/30 rounded-lg p-3 w-full text-left hover:bg-accent/50 transition-colors"
              >
                <p className="text-xs text-muted-foreground mb-2">Produto de interesse:</p>
                <div className="flex gap-3">
                  <AdImage
                    src={getAdImage(offer.targetAd)}
                    alt={offer.targetAd.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-medium text-sm">{offer.targetAd.title}</h3>
                    <p className="text-lg font-semibold text-primary">{formatCurrency(offer.targetAd.price)}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Clique para ver detalhes →</p>
                  </div>
                </div>
              </button>

              {offer.parentOfferId && (
                <p className="text-xs text-muted-foreground pl-2 border-l-2 border-primary/50">Contra-oferta (atual)</p>
              )}

              {/* Offer Details */}
              <div className="space-y-3">
                <p className="text-sm font-medium">{offer.parentOfferId ? "Detalhes da Contra-Oferta" : "Detalhes da Oferta"}:</p>

                {offer.tradeInAds && offer.tradeInAds.length > 0 && (
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">
                      {offer.tradeInAds.length === 1 ? "Produto oferecido:" : "Produtos oferecidos:"}
                    </p>
                    <div className="space-y-2">
                      {offer.tradeInAds.map((tradeAd) => (
                        <div key={tradeAd.id} className="flex gap-2">
                          <AdImage
                            src={tradeAd.image}
                            alt={tradeAd.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div>
                            <p className="text-sm font-medium">{tradeAd.title}</p>
                            <p className="text-sm text-primary">{formatCurrency(tradeAd.value)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-background rounded-lg p-3 space-y-2">
                  {offer.tradeInAds && offer.tradeInAds.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Valor dos produtos:</span>
                      <span className="font-medium">
                        {formatCurrency(offer.tradeInAds.reduce((sum, ad) => sum + ad.value, 0))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Pagamento em dinheiro:</span>
                    <span className="font-medium">{formatCurrency(offer.cashAmount)}</span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-medium">Valor total da oferta:</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(offer.totalOfferValue)}
                      </span>
                    </div>
                  </div>
                </div>

                {offer.message && (
                  <div className="bg-accent/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Mensagem:</p>
                    <p className="text-sm">{offer.message}</p>
                  </div>
                )}
              </div>

              {/* Status Timeline */}
              {offer.updates && offer.updates.length > 1 && (
                <div className="bg-accent/20 rounded-lg p-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Histórico de Atualizações
                  </p>
                  <div className="space-y-3">
                    {offer.updates.slice().reverse().map((update, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            update.type === "accepted" ? "bg-green-500/20" :
                            update.type === "rejected" ? "bg-red-500/20" :
                            update.type === "counter-offer-received" ? "bg-blue-500/20" :
                            "bg-gray-500/20"
                          }`}>
                            {update.type === "accepted" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                            {update.type === "rejected" && <XCircle className="w-4 h-4 text-red-600" />}
                            {update.type === "counter-offer-received" && <MessageSquare className="w-4 h-4 text-blue-600" />}
                            {update.type === "sent" && <Clock className="w-4 h-4 text-gray-600" />}
                          </div>
                          {index < offer.updates.length - 1 && (
                            <div className="w-0.5 h-8 bg-border" />
                          )}
                        </div>
                        <div className="flex-1 pb-2">
                          <p className="text-sm font-medium">
                            {update.type === "sent" && "Oferta enviada"}
                            {update.type === "accepted" && "Oferta aceita pelo vendedor"}
                            {update.type === "rejected" && "Oferta recusada pelo vendedor"}
                            {update.type === "counter-offer-received" && "Contra-oferta recebida"}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatDate(update.timestamp)}</p>
                          {update.message && (
                            <p className="text-xs mt-1 bg-background rounded p-2">{update.message}</p>
                          )}
                          {update.counterOfferAmount && (
                            <p className="text-xs mt-1 bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded p-2">
                              Novo valor: {formatCurrency(update.counterOfferAmount)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {offer.status === "pending" && offer.parentOfferId && (
                accountNav ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleAcceptOffer(offer.id)}
                      disabled={acceptingOfferId !== null}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {acceptingOfferId === offer.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5" />
                      )}
                      Aceitar Contra-Oferta
                    </button>
                    <button
                      onClick={() => openWithdrawConfirm(offer.id)}
                      disabled={withdrawingOfferId !== null}
                      className="w-full bg-destructive hover:bg-destructive/90 disabled:opacity-60 text-destructive-foreground py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {withdrawingOfferId === offer.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                      Retirar Oferta
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 p-3 text-center">
                    Complete seu cadastro para aceitar a contra-oferta ou retirar sua oferta.
                  </p>
                )
              )}
              {offer.status === "pending" && !offer.parentOfferId && (
                accountNav ? (
                  <button
                    onClick={() => openWithdrawConfirm(offer.id)}
                    disabled={withdrawingOfferId !== null}
                    className="w-full bg-destructive hover:bg-destructive/90 disabled:opacity-60 text-destructive-foreground py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {withdrawingOfferId === offer.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                    Retirar Oferta
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 p-3 text-center">
                    Complete seu cadastro para retirar sua oferta.
                  </p>
                )
              )}
              {offer.status === "accepted" &&
                offer.isCurrentUserBuyer &&
                accountNav &&
                !hideOfferCartActions &&
                (onAddOfferToCart || onRemoveOfferFromCart) && (
                <div className="space-y-2">
                  {inCartForThisThread ? (
                    <Button
                      onClick={() => {
                        const idToRemove = offerIdsInCart.includes(offer.id)
                          ? offer.id
                          : offer.parentOfferId ?? offer.id;
                        onRemoveOfferFromCart?.(idToRemove);
                      }}
                      variant="outline"
                      className="w-full py-3 rounded-lg font-medium border-destructive text-destructive hover:bg-destructive/10 flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-5 h-5" />
                      Remover do Carrinho
                    </Button>
                  ) : (
                    <Button
                      onClick={() => onAddOfferToCart?.(offer.id)}
                      className="w-full py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: 'var(--orange)',
                        color: 'var(--orange-foreground)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--orange-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--orange)';
                      }}
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Adicionar ao Carrinho
                    </Button>
                  )}
                </div>
              )}
              </div>
            );
            if (item.type === "single") {
              return <div key={`single-${offer.id}`}>{card}</div>;
            }
            const parentId = item.root?.id ?? item.currentOffer.parentOfferId ?? "";
            const rootOfferFromList = parentId ? filteredOffers.find((o) => o.id === parentId) : undefined;
            return (
              <CollapsibleNestedOffer
                key={`thread-${parentId}-${offer.id}`}
                parentOfferId={parentId}
                defaultExpanded={false}
                collapsibleLabel="Oferta original"
                collapsibleContent={
                  <ParentOfferBlock
                    parentOfferId={parentId}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    label="Oferta original"
                    totalOfferValue={rootOfferFromList?.totalOfferValue}
                  />
                }
                wrapperClassName="ml-4 pl-4 border-l-2 border-primary/30"
              >
                {card}
              </CollapsibleNestedOffer>
            );
          })
        )}
      </div>

      <WithdrawOfferConfirmToast
        isOpen={showWithdrawConfirmToast && accountNav}
        onClose={closeWithdrawConfirmToast}
        onConfirm={handleWithdrawConfirm}
        isConfirming={withdrawingOfferId !== null}
      />

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}